import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import ApiClient from '@src/lib/api_client'

import Note from '@src/data/note'
import { ErrorMessage, validateRequiredField } from '@src/util/validation'
import { Button } from '@src/elements/button/button'
import { Input } from '@src/elements/input/input'
import { snakecaseKeys } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import { Modal } from '@src/elements/modal/modal'
import { Textarea } from '@src/elements/input/textarea'
import { Divider } from '@src/elements/divider/divider'
import { Body2 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import EventBus from '@src/lib/event_bus'

import './create.sass'

@Component({
  computed: {
    note () {
      return this.$store.state.note
    }
  }
})
export class CreateNote extends Vue {

  protected note: Note | null

  protected errorCodes: ErrorMessage[] = []

  protected tagsInputValue: string = ''

  protected apiClient: ApiClient

  private isFormVisible: boolean = false

  constructor () {
    super()
    this.apiClient = new ApiClient(API.TRAINERS_API.MEMBERS)
  }

  save () {
    this.validateForm()

    if (this.errorCodes.length === 0) {
      if (this.note!.id) {
        return this.editNote(this.note)
      } else {
        return this.createNote(this.note)
      }
    }
  }

  createNote (note) {
    return this.apiClient.post(`${this.$route.params.id}/notes`, { note: snakecaseKeys(note) })
      .then((response: any) => {
        EventBus.trigger('reloadNotes')
        this.$store.dispatch('setNote', null)
      })
      .catch((error) => {
        console.log('cannot create note', error)
      })
  }

  editNote (note) {
    // delete note.id
    return this.apiClient.put(`${this.$route.params.id}/notes/${note.id}`, { note: snakecaseKeys(note) })
      .then((response: any) => {
        EventBus.trigger('reloadNotes')
        this.$store.dispatch('setNote', null)
      })
      .catch((error) => {
        console.log('cannot edit note', error)
      })
  }

  checkAddTag (tag: string) {
    if ([' ', ','].includes(tag[tag.length - 1])) {
      this.addTag(tag)
    } else {
      this.tagsInputValue = tag
    }
  }

  addTag (tag: string) {
    let cleanValue = tag.replace(/^[, ]+|[, ]+$|[, ]+/g, ' ').trim()
    if (cleanValue) {
      this.note!.tags.push(cleanValue)
      this.tagsInputValue = ''
    }
  }

  showAddForm () {
    this.isFormVisible = true

    if (!this.note) {
      // initialize note
      this.$store.dispatch('setNote', new Note({ createdAt: new Date(), tags: [], text: '' }))
    }
  }

  hideAddForm () {
    this.isFormVisible = false
    this.$store.dispatch('setNote', null)
  }

  @Watch('note')
  onNote (note: Note | null) {
    if (note) {
      this.showAddForm()
    }
  }

  removeTag (index: number) {
    this.note!.tags.splice(index, 1)
  }

  render (h: CreateElement) {

    const tags = !this.note ? [] : this.note!.tags.map((tag: string, index: number) => {
      return <Body2 class='note-tag'>
        {tag}
        <Icon name='util/close-circled' click={() => this.removeTag(index)} /></Body2>
    })

    return <div class='create-note'>
      <Button type={'elevated'} label={this.$t('PAGE.MEMBERS.NOTES.CREATE_NOTE')} click={this.showAddForm} ref={'showPopupBtn'} class={'show-popup-btn'} />

      {this.isFormVisible && this.note ?
        <Modal show={true} title={this.$t('PAGE.MEMBERS.NOTES.CREATE_NOTE')} closeCallback={this.hideAddForm}>

          <Input ref='noteTags' class={(this.hasError('TAGS') ? 'error' : '')} icon='util/label' label={this.$t('PAGE.MEMBERS.NOTES.ENTER_NOTE_TAGS')}
                 valueChanged={this.checkAddTag} focusLost={this.addTag} value={this.tagsInputValue} />

          <div class='row tag-list'>
            {tags}
          </div>

          <Divider />

          <Textarea value={this.note!.text} class={this.hasError('NOTES') ? 'error' : null} label={this.$t('PAGE.MEMBERS.NOTES.WRITE_NOTE')}
                    change={(value) => this.note!.text = value} />

          <div class='btn-row' slot='footer'>
            <Button type={'elevated'} ref='saveBtn' label={this.note!.id ? this.$t('SAVE') : this.$t('PAGE.MEMBERS.NOTES.ADD_NOTE')} click={this.save} />
          </div>
        </Modal>
        : null}
    </div>
  }

  hasError (fieldName: string): boolean {
    return this.errorCodes.find(o => {
      let field = (o.fieldName === undefined ? '' : o.fieldName).toLowerCase()
      return field.indexOf(fieldName.toLowerCase()) >= 0
    }) !== undefined
  }

  validateForm () {
    let errorCodes: any = []

    let errorCode = validateRequiredField(this.note!.text, 'NOTES')
    if (errorCode) {
      errorCodes.push(errorCode)
    }
    this.errorCodes = errorCodes
  }

  beforeDestroy () {
    this.$store.dispatch('setNote', null)
  }
}
